import { DateTime } from 'luxon';
import {
  GET_EQUIPMENT,
  CREATE_EQUIPMENT,
  ARCHIVE_EQUIPMENT,
  DELETE_EQUIPMENT,
  UPDATE_EQUIPMENT,
  GET_EQUIPMENT_HISTORY,
  CHECK_IN_EQUIPMENT,
  CHECK_OUT_EQUIPMENT,
  GET_EQUIPMENT_FILES,
  GET_EQUIPMENT_NOTES,
  ADD_EQUIPMENT_NOTE,
  GET_EQUIPMENT_STATUSES,
  GET_EQUIPMENT_TYPES,
  GET_EQUIPMENT_CUSTOM_DATA,
  GET_EQUIPMENT_CUSTOM_DATA_MAP,
  GET_MY_EQUIPMENT_TAGS,
  MARK_EQUIPMENT_TAGS_AS_READ,
} from '../../state/actionTypes';
import Analytics from '../../helpers/Analytics';
import EquipmentService from './equipment.service';
import { decorateFormWithFiles } from '../../forms/formHelpers';
import { counters, counterWasRecentlyCalled } from '../../helpers/counters';

const {
  equipment: equipmentCounters,
} = counters;

export const createEquipment = (payload) => async (dispatch) => {
  Analytics.track('Equipment/Create');
  const { data } = await EquipmentService.create({
    ...payload,
    labels: payload.labels?.map((label) => ({
      title: label.title,
      labelId: label.id,
    })) ?? [],
  });
  if (!data) return false;
  dispatch({
    type: CREATE_EQUIPMENT,
    payload: {
      equipment: data,
      files: payload.files,
    },
  });
  return true;
};

export const getEquipment = (shouldIgnoreCounter = false) => async (dispatch) => {
  if (!shouldIgnoreCounter && counterWasRecentlyCalled('equipment.getEquipment')) return true;
  equipmentCounters.getEquipment = DateTime.local();
  const { data } = await EquipmentService.get();
  if (!data) {
    equipmentCounters.getEquipment = null;
    return false;
  }
  dispatch({
    type: GET_EQUIPMENT,
    payload: {
      equipment: data,
    },
  });
  return true;
};

export const archiveEquipment = (id, active = false) => async (dispatch) => {
  Analytics.track('Equipment/Archive', { active });
  const didArchive = await EquipmentService.archive(id, active);
  if (!didArchive) return false;
  dispatch({
    type: ARCHIVE_EQUIPMENT,
    payload: {
      id,
      active,
    },
  });
  return true;
};

export const bulkArchiveEquipment = (ids, { password }, active = false) => async (dispatch) => {
  Analytics.track('Equipment/BulkArchive', { active, count: ids.length });
  const { data, err } = await EquipmentService.bulkArchive(ids, { password, active });
  if (err || !data) return false;
  (data.updatedIds || []).forEach((id) => {
    dispatch({
      type: ARCHIVE_EQUIPMENT,
      payload: { id, active },
    });
  });
  return true;
};

export const deleteEquipment = (id) => async (dispatch) => {
  Analytics.track('Equipment/Delete');
  const didDelete = await EquipmentService.deleteEquipment(id);
  if (!didDelete) return false;
  dispatch({
    type: DELETE_EQUIPMENT,
    payload: {
      id,
    },
  });
  return true;
};

export const bulkDeleteEquipment = (ids, { password }) => async (dispatch) => {
  Analytics.track('Equipment/BulkDelete', { count: ids.length });
  const { data, err } = await EquipmentService.bulkDelete(ids, { password });
  if (err || !data) return false;
  (data.deletedIds || []).forEach((id) => {
    dispatch({
      type: DELETE_EQUIPMENT,
      payload: { id },
    });
  });
  return true;
};

export const updateEquipment = (id, payload) => async (dispatch) => {
  Analytics.track('Equipment/Update');
  const { data } = await EquipmentService.update(id, payload);
  if (!data) return false;
  dispatch({
    type: UPDATE_EQUIPMENT,
    payload: {
      id,
      newData: {
        ...payload,
        hasImage: !!payload.image,
      },
      createdLabels: data,
    },
  });
  return true;
};

export const getEquipmentHistory = (id) => async (dispatch) => {
  const { data } = await EquipmentService.getHistory(id);
  if (!data) return false;
  dispatch({
    type: GET_EQUIPMENT_HISTORY,
    payload: {
      id,
      ...data,
    },
  });
  return true;
};

export const checkIn = (id, payload) => async (dispatch) => {
  const { data } = await EquipmentService.checkIn(id, payload);
  if (!data) return false;
  dispatch({
    type: CHECK_IN_EQUIPMENT,
    payload: data,
  });
  return true;
};

export const checkOut = (id, payload) => async (dispatch) => {
  const { data } = await EquipmentService.checkOut(id, payload);
  if (!data) return false;
  dispatch({
    type: CHECK_OUT_EQUIPMENT,
    payload: data,
  });
  return true;
};

export const getEquipmentFiles = (id) => async (dispatch) => {
  const { data: files } = await EquipmentService.getFiles(id);
  if (!files) return false;
  dispatch({
    type: GET_EQUIPMENT_FILES,
    payload: {
      id,
      files,
    },
  });
  return true;
};

export const getNotes = (id) => async (dispatch) => {
  const { data } = await EquipmentService.getNotes(id);
  if (!data) return false;
  dispatch({
    type: GET_EQUIPMENT_NOTES,
    payload: {
      id,
      data,
    },
  });
  return true;
};

export const addNote = ({ id, text, tags, files }) => async (dispatch) => {
  const { data } = await EquipmentService.addNote(id, { text, tags, files });
  if (!data) return false;
  dispatch({
    type: ADD_EQUIPMENT_NOTE,
    payload: data,
  });
  return true;
};

export const getStatuses = () => async (dispatch) => {
  const { data } = await EquipmentService.getStatuses();
  if (!data) return false;
  dispatch({
    type: GET_EQUIPMENT_STATUSES,
    payload: {
      statuses: data,
    },
  });
  return true;
};

export const getEquipmentTypes = () => async (dispatch) => {
  if (counterWasRecentlyCalled('equipment.getEquipmentTypes')) return true;
  equipmentCounters.getEquipmentTypes = DateTime.local();
  const { data } = await EquipmentService.getEquipmentTypes();
  if (!data) {
    equipmentCounters.getEquipmentTypes = null;
    return false;
  }
  dispatch({
    type: GET_EQUIPMENT_TYPES,
    payload: {
      customTypes: data,
    },
  });
  return true;
};

export const getEquipmentCustomData = (id) => async (dispatch) => {
  if (!id) return dispatch({ type: GET_EQUIPMENT_CUSTOM_DATA, payload: { data: [] } });

  const { data } = await EquipmentService.getEquipmentCustomData(id);
  await decorateFormWithFiles({ fileMap: data.fileMap });

  if (!data) return false;
  dispatch({
    type: GET_EQUIPMENT_CUSTOM_DATA,
    payload: {
      data,
    },
  });
  return true;
};

export const getEquipmentCustomDataMap = (params) => async (dispatch) => {
  if (!params.ids && !params.projectId) {
    return dispatch({ type: GET_EQUIPMENT_CUSTOM_DATA_MAP, payload: { data: [] } });
  }

  const { data } = await EquipmentService.getEquipmentCustomDataMap(params);

  if (!data) return false;
  dispatch({
    type: GET_EQUIPMENT_CUSTOM_DATA_MAP,
    payload: {
      data,
    },
  });
  return data;
};

export const getMyEquipmentTags = ({ hasRead = false } = {}) => async (dispatch) => {
  const { data } = await EquipmentService.getMyEquipmentTags(hasRead);
  if (!data) return false;
  dispatch({
    type: GET_MY_EQUIPMENT_TAGS,
    payload: {
      tags: data,
      hasRead,
    },
  });
  return true;
};

export const markEquipmentTagsAsRead = (noteIds) => async (dispatch) => {
  const { data } = await EquipmentService.markEquipmentTagsAsRead(noteIds);
  if (!data) return false;
  dispatch({
    type: MARK_EQUIPMENT_TAGS_AS_READ,
    payload: {
      noteIds: new Set(noteIds),
    },
  });
  return true;
};
